'use client';
import React from 'react';
import AppImage from '@/components/ui/AppImage';
import { Users, Zap, Fuel } from 'lucide-react';
import type { Car } from '@/lib/supabase/dinauto-service';

interface CarCardProps {
  car: Car;
  onReserve: (car: Car) => void;
  onViewDetail: (car: Car) => void;
}

export default function CarCard({ car, onReserve, onViewDetail }: CarCardProps) {
  const mainImage = car.images.find((i) => i.isMain) || car.images[0];
  const isReserved = car.status === 'Reserved';
  const isHidden = car.status === 'Hidden';

  if (isHidden) return null;

  return (
    <div
      className="bg-white border border-border car-card-hover cursor-pointer group"
      onClick={() => onViewDetail(car)}
    >
      {/* Image */}
      <div className="relative overflow-hidden h-48 bg-muted">
        <AppImage
          src={mainImage.url}
          alt={mainImage.alt}
          fill
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          className="object-cover transition-transform duration-300 group-hover:scale-105"
        />
        {/* Category badge */}
        <div className="absolute top-3 left-3">
          <span className={
            car.category === 'Economy' ? 'badge-economy' :
            car.category === 'Comfort' ? 'badge-comfort' : 'badge-suv'
          }>
            {car.category}
          </span>
        </div>
        {/* Status overlay */}
        {isReserved && (
          <div className="absolute inset-0 bg-foreground/60 flex items-center justify-center">
            <span className="bg-foreground text-white text-xs font-bold uppercase tracking-widest px-4 py-2 border border-white/20">
              Reserved
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">{car.brand}</p>
            <h3 className="font-black text-lg leading-tight" style={{ fontWeight: 800 }}>
              {car.model}{car.year ? ` ${car.year}` : ''}
            </h3>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground">from</p>
            <p className="text-xl font-black tabular-nums" style={{ fontWeight: 900 }}>
              {car.pricePerDay.toLocaleString('no-NO')}
              <span className="text-sm font-600 text-muted-foreground"> NOK</span>
            </p>
            <p className="text-xs text-muted-foreground">per day</p>
          </div>
        </div>

        {/* Specs */}
        <div className="flex gap-4 py-3 border-t border-border text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <Users size={13} />
            {car.seats} seats
          </span>
          <span className="flex items-center gap-1.5">
            <Zap size={13} />
            {car.gearbox}
          </span>
          <span className="flex items-center gap-1.5">
            <Fuel size={13} />
            {car.fuel}
          </span>
        </div>

        {/* Action */}
        <button
          className={`btn-primary w-full mt-3 text-sm py-2.5 ${isReserved ? 'opacity-40 cursor-not-allowed' : ''}`}
          disabled={isReserved}
          onClick={(e) => {
            e.stopPropagation();
            if (!isReserved) onReserve(car);
          }}
        >
          {isReserved ? 'Not Available' : 'Reserve This Car'}
        </button>
      </div>
    </div>
  );
}